// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Statistiques — pages Statistiques (admin), Mes statistiques (tous), et les
// tableaux de consommation / résultats par règle partagés avec le portail
// licence.
//
// ⚠️ Les clés `type_rapport_*` et `call_type_*` sont résolues DYNAMIQUEMENT :
// `t(\`type_rapport_${tr}\`)` dans AdminStats, MesStatistiques et LicensePortal.
// Une recherche de `t('type_rapport_dta')` ne les trouve donc pas — ne pas
// conclure qu'elles sont mortes, et ne jamais en renommer une sans vérifier la
// valeur qui alimente la substitution (côté API : `analyses.type_rapport` et
// `usage_events.call_type`).
//
// ⚠️ Depuis le 28/07/2026, les vues client (Mes statistiques, portail licence)
// n'affichent plus ni tokens ni typologie d'appel. Les clés correspondantes
// (`stat_col_tokens_in/out`, `stat_token_page_ratio`, `stat_all_call_types`,
// `stat_col_call_type`, `call_type_*`, `stat_avg_pages`…) restent VIVANTES :
// `AdminStats.js` les utilise toujours. Ne pas les supprimer sur la foi d'une
// recherche dans les seules pages client.

const statistiques = {
  fr: {
    // ── Cartes de synthèse ──
    stat_total:                        'Total analyses',
    stat_completed:                    'Terminées',
    stat_error:                        'En erreur',
    stat_in_progress:                  'En cours',
    stat_avg_score:                    'Score moyen',
    stat_avg_score_tooltip:            'Score indicatif de qualité normative des rapports analysés. Chaque avis émis par NORMA retranche des points, selon la gravité de la conséquence potentielle. Ce n\'est pas un taux de conformité : deux rapports au même score peuvent porter des écarts de nature très différente. Non applicable aux missions DTA/EP/Fiche récap.',
    stat_avg_duration:                 'Durée moyenne',
    stat_users:                        'Utilisateurs',
    stat_by_week:                      'Analyses par semaine (8 dernières)',
    stat_top_users:                    'Top 5 utilisateurs',
    stat_success_pct:                  '% de succès',
    week_prefix:                       'S',

    // ── Mes statistiques (périmètre personnel) ──
    my_stats_title:                    'Mes statistiques',
    my_stats_first_analysis:           'Première analyse',
    my_stats_last_analysis:            'Dernière analyse',

    // ── Consommations ──
    stat_consommations_title:          '📊 Consommations',
    stat_scope_total:                  'Total',
    stat_scope_user:                   'Par user',
    stat_scope_tier:                   'Par licence',
    stat_choose_placeholder:           '— Choisir —',
    stat_avg_pages:                    'Nombre de pages moyen',
    stat_token_page_ratio:             'Ratio tokens / page',
    stat_filter_by_typology:           'Filtrer par typologie…',
    stat_col_call_type:                "Typologie d'appel",
    stat_col_occurrences:              'Occurrences',
    stat_col_tokens_in:                'Tokens input (moyenne)',
    stat_col_tokens_out:               'Tokens output (moyenne)',
    stat_consommations_empty:          'Aucune consommation pour ce périmètre (ne couvre que les analyses lancées après le déploiement du tracking).',

    // ── Consommations, vue client (Mes statistiques + portail licence) ──
    // Vocabulaire métier : l'utilisateur achète des analyses, pas des appels LLM.
    stat_periode_mensuelle:            'Vue mensuelle',
    stat_periode_annuelle:             'Vue annuelle',
    stat_avg_pages_analysees:          'Nombre moyen de pages analysées',
    stat_total_analyses_periode:       'Analyses sur la période',
    stat_total_pages_ocr_periode:      'Pages OCR sur la période',
    stat_serie_analyses:               'Analyses',
    stat_serie_pages_ocr:              'Pages OCR',
    stat_axe_jour:                     'Jour',
    stat_axe_mois:                     'Mois',
    stat_graph_aria:                   'Graphique en barres : nombre d\'analyses et nombre de pages OCR sur la période choisie',

    // ── Typologies d'appel (résolues dynamiquement) ──
    call_type_pass1:                   'Pass 1 — Structuration',
    call_type_pass1_retry:             'Pass 1 — Retry validation',
    call_type_pass2_metadata:          'Pass 2 — Métadonnées',
    call_type_pass2_samples:           'Pass 2 — Échantillons',
    call_type_pass2_conclusions:       'Pass 2 — Conclusions',
    call_type_pass2_sections:          'Pass 2 — Sections',
    call_type_pass2_global:            'Pass 2 — Cohérence globale',
    call_type_annexe:                  'Annexes (assurance/certif/labo)',
    call_type_contextuel_interne:      'Contextuel — Programme du rapport',
    call_type_rag_coherence_externe:   'RAG — Cohérence externe',
    call_type_rag_ingestion_think:     'RAG — Ingestion (analyse)',
    call_type_rag_ingestion_extract:   'RAG — Ingestion (extraction)',
    call_type_zpso:                    'ZPSO — Rattachement des prélèvements',
    call_type_ocr:                     'OCR',
    call_type_non_categorise:          'Non catégorisé (historique)',

    // ── Résultats par règle ──
    stat_resultats_title:              '📋 Résultats des analyses',
    stat_all_users:                    'Tous les users',
    stat_all_tiers:                    'Toutes licences',
    stat_all_type_rapports:            'Tous types de rapport',
    stat_all_call_types:               "Tous types d'appel",
    stat_all_rules:                    'Toutes les règles',
    stat_avec_sans_projet:             'Avec/sans projet',
    stat_avec_projet:                  'Avec projet',
    stat_sans_projet:                  'Sans projet',
    stat_all_societes:                 'Toutes sociétés de diagnostic',
    stat_nb_rapports:                  'Rapports analysés',
    stat_avg_favorable:                'Avis favorables / rapport',
    stat_avg_reserve:                  'Réserves / rapport',
    stat_avg_defavorable:              'Défavorables / rapport',
    stat_filter_by_rule:               'Filtrer par règle…',
    stat_col_rule_number:              'Règle #',
    stat_col_rule_name:                'Nom de la règle',
    stat_col_total_avis:               'Total avis',
    stat_col_pct_favorable:            '% Favorable',
    stat_col_pct_reserve:              '% Réserve',
    stat_col_pct_defavorable:          '% Défavorable',
    stat_resultats_empty:              'Aucun résultat pour ce périmètre (ne couvre que les analyses lancées après le déploiement du tracking).',

    // ── Types de rapport (résolus dynamiquement) ──
    type_rapport_avant_travaux:        'Avant travaux',
    type_rapport_avant_demolition:     'Avant démolition',
    type_rapport_avant_vente:          'Avant vente',
    type_rapport_dta:                  'DTA',
    type_rapport_dapp:                 'DAPP',
    type_rapport_evaluation_periodique:'Évaluation périodique',
    type_rapport_autre:                'Hors champ',
  },

  en: {
    // ── Summary cards ──
    stat_total:                        'Total analyses',
    stat_completed:                    'Completed',
    stat_error:                        'Errors',
    stat_in_progress:                  'In progress',
    stat_avg_score:                    'Avg score',
    stat_avg_score_tooltip:            'Indicative score of the normative quality of the analysed reports. Each assessment issued by NORMA deducts points, according to the severity of the potential consequence. This is not a compliance rate: two reports with the same score may carry very different kinds of discrepancy. Not applicable to DTA/EP/Summary sheet missions.',
    stat_avg_duration:                 'Avg duration',
    stat_users:                        'Users',
    stat_by_week:                      'Analyses per week (last 8)',
    stat_top_users:                    'Top 5 users',
    stat_success_pct:                  '% success rate',
    week_prefix:                       'W',

    // ── My statistics (personal scope) ──
    my_stats_title:                    'My statistics',
    my_stats_first_analysis:           'First analysis',
    my_stats_last_analysis:            'Last analysis',

    // ── Usage ──
    stat_consommations_title:          '📊 Usage',
    stat_scope_total:                  'Total',
    stat_scope_user:                   'By user',
    stat_scope_tier:                   'By plan',
    stat_choose_placeholder:           '— Choose —',
    stat_avg_pages:                    'Average page count',
    stat_token_page_ratio:             'Tokens / page ratio',
    stat_filter_by_typology:           'Filter by call type…',
    stat_col_call_type:                'Call type',
    stat_col_occurrences:              'Occurrences',
    stat_col_tokens_in:                'Input tokens (average)',
    stat_col_tokens_out:               'Output tokens (average)',
    stat_consommations_empty:          'No usage data for this scope (only covers analyses run since the tracking was deployed).',

    // ── Usage, client view (My statistics + license portal) ──
    // Business wording: users buy analyses, not LLM calls.
    stat_periode_mensuelle:            'Monthly view',
    stat_periode_annuelle:             'Yearly view',
    stat_avg_pages_analysees:          'Average number of pages analyzed',
    stat_total_analyses_periode:       'Analyses in period',
    stat_total_pages_ocr_periode:      'OCR pages in period',
    stat_serie_analyses:               'Analyses',
    stat_serie_pages_ocr:              'OCR pages',
    stat_axe_jour:                     'Day',
    stat_axe_mois:                     'Month',
    stat_graph_aria:                   'Bar chart: number of analyses and number of OCR pages over the selected period',

    // ── Call types (resolved dynamically) ──
    call_type_pass1:                   'Pass 1 — Structuring',
    call_type_pass1_retry:             'Pass 1 — Retry validation',
    call_type_pass2_metadata:          'Pass 2 — Metadata',
    call_type_pass2_samples:           'Pass 2 — Samples',
    call_type_pass2_conclusions:       'Pass 2 — Conclusions',
    call_type_pass2_sections:          'Pass 2 — Sections',
    call_type_pass2_global:            'Pass 2 — Global coherence',
    call_type_annexe:                  'Annexes (insurance/certification/lab)',
    call_type_contextuel_interne:      'Contextual — Report’s own programme',
    call_type_rag_coherence_externe:   'RAG — External coherence',
    call_type_rag_ingestion_think:     'RAG — Ingestion (analysis)',
    call_type_rag_ingestion_extract:   'RAG — Ingestion (extraction)',
    call_type_zpso:                    'ZPSO — Sample attachment',
    call_type_ocr:                     'OCR',
    call_type_non_categorise:          'Uncategorized (legacy)',

    // ── Results by rule ──
    stat_resultats_title:              '📋 Analysis results',
    stat_all_users:                    'All users',
    stat_all_tiers:                    'All plans',
    stat_all_type_rapports:            'All report types',
    stat_all_call_types:               'All call types',
    stat_all_rules:                    'All rules',
    stat_avec_sans_projet:             'With/without project',
    stat_avec_projet:                  'With project',
    stat_sans_projet:                  'Without project',
    stat_all_societes:                 'All diagnostic companies',
    stat_nb_rapports:                  'Reports analyzed',
    stat_avg_favorable:                'Favorable opinions / report',
    stat_avg_reserve:                  'Reserves / report',
    stat_avg_defavorable:              'Unfavorable opinions / report',
    stat_filter_by_rule:               'Filter by rule…',
    stat_col_rule_number:              'Rule #',
    stat_col_rule_name:                'Rule name',
    stat_col_total_avis:               'Total opinions',
    stat_col_pct_favorable:            '% Favorable',
    stat_col_pct_reserve:              '% Reserve',
    stat_col_pct_defavorable:          '% Unfavorable',
    stat_resultats_empty:              'No data for this scope (only covers analyses run since the tracking was deployed).',

    // ── Report types (resolved dynamically) ──
    type_rapport_avant_travaux:        'Before works',
    type_rapport_avant_demolition:     'Before demolition',
    type_rapport_avant_vente:          'Before sale',
    type_rapport_dta:                  'DTA',
    type_rapport_dapp:                 'DAPP',
    type_rapport_evaluation_periodique:'Periodic assessment',
    type_rapport_autre:                'Out of scope',
  },
};

export default statistiques;
